import { useCallback, useMemo, useState } from "react";
import { styled, Typography } from "@mui/material";
import { PersonOutline, KeyboardArrowRight } from "@mui/icons-material";
import {
  PatientsListCard,
  PatientsListCardHeader,
  PatientsListCardContent,
  AddPatientButton,
  StyledAvatar,
  Search,
  PatientListItem,
  PatientListItemContent,
} from "../styled";
import type { Request, Patient } from "@interfaces";
import { RequestType } from "@interfaces";
import { format } from "rut.js";
import Fuse from "fuse.js";
import { useNavigate } from "react-router-dom";

const ArrowForwardIcon = styled(KeyboardArrowRight)(({ theme }) => ({
  color: theme.palette.icon.primary,
}));

interface Props {
  requests: Request[];
}

export const PatientsListComponent: React.FC<Props> = ({ requests }) => {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const allPatients = useMemo(
    () =>
      (requests ?? [])
        .map((request) => request.patient)
        .filter(
          (patient, index, array) =>
            array.findIndex(
              (p) =>
                (p.rut === patient.rut || p.passport === patient.passport) &&
                p.name === patient.name,
            ) === index,
        ),
    [requests],
  );

  const fusePatients = useMemo(
    () =>
      new Fuse(
        allPatients.map((patient) => {
          return {
            ...patient,
            fRut: patient.rut
              ? format(patient.rut)
              : patient.passport
                ? patient.passport
                : "",
            cleanRut: patient.rut
              ? patient.rut.replace(/[.-]/g, "")
              : patient.passport
                ? patient.passport
                : "",
          };
        }),
        {
          keys: ["name", "firstSurname", "secondSurname", "fRut", "cleanRut", "rut"],
          threshold: 0.1,
        },
      ),
    [allPatients],
  );

  const patients = useMemo(
    () =>
      search.length > 0
        ? fusePatients.search(search).map((result) => result.item)
        : allPatients,
    [search, fusePatients, allPatients],
  );

  const onAddPatientClick = useCallback(() => {
    navigate("/app/nuevo-documento/paciente/agregar", {
      state: {
        documentType: RequestType.PRESCRIPTION,
        ...(/^[\d.-]+$/.test(search) && { searchedRut: search }),
      },
    });
  }, [navigate, search]);

  const onPatientClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, patient: Patient) => {
      e.stopPropagation();
      navigate(`/app/pacientes/${patient.rut}`);
    },
    [navigate],
  );

  return (
    <PatientsListCard elevation={0}>
      <PatientsListCardHeader
        avatar={
          <StyledAvatar>
            <PersonOutline sx={(theme) => ({ color: theme.palette.icon.primary })} />
          </StyledAvatar>
        }
        title={
          <Typography variant="h4" fontWeight={500}>
            Mis pacientes
          </Typography>
        }
      />
      <PatientsListCardContent>
        <AddPatientButton
          variant="outlined"
          color="primary"
          fullWidth
          onClick={onAddPatientClick}
        >
          Agregar paciente
        </AddPatientButton>

        {allPatients.length > 0 && (
          <>
            <Search
              placeholder="Buscar por nombre o RUT"
              searchText={search}
              setSearchText={setSearch}
              showSuggestions={false}
              obscureInTracker
            />

            {patients.map((patient) => (
              <PatientListItem
                key={patient.rut}
                onClick={(e) => onPatientClick(e, patient)}
              >
                <PatientListItemContent>
                  <Typography variant="h5">
                    {patient.firstSurname} {patient.secondSurname}, {patient.name}
                  </Typography>
                  <Typography variant="body1" color="text.gray">
                    {patient.rut || patient.passport}
                  </Typography>
                </PatientListItemContent>
                <ArrowForwardIcon />
              </PatientListItem>
            ))}
          </>
        )}
      </PatientsListCardContent>
    </PatientsListCard>
  );
};
